import React, { useState, useEffect } from 'react';
import { Modal, Table, Input, Button, Tag, Space, Form, message, Select } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';
import { fetchCustomerVehicles, searchCustomerVehicles } from '../services/serviceOrderService';
import { trLower, trUpperNormalize } from '../utils/trLocale';

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  accountType: string;
  customerCategory?: string;
  email?: string;
  phone?: string;
}

interface AccountSelectModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (account: Account) => void;
  /** true = Yeni Cari Ekle modal içinde açılır, false = /add-vehicle-with-customer sayfasına gider */
  inlineAddAccount?: boolean;
}

const AccountSelectModal: React.FC<AccountSelectModalProps> = ({ visible, onClose, onSelect, inlineAddAccount = true }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [platesByAccountId, setPlatesByAccountId] = useState<Record<number, string[]>>({});
  const [loadingPlates, setLoadingPlates] = useState<Record<number, boolean>>({});
  const [addAccountModalVisible, setAddAccountModalVisible] = useState(false);
  const [addAccountSaving, setAddAccountSaving] = useState(false);
  const [accountForm] = Form.useForm();
  const [customerCategories, setCustomerCategories] = useState<Array<{ value: number; name: string; displayName: string }>>([]);

  useEffect(() => {
    if (visible) {
      loadAccounts();
    }
  }, [visible]);

  useEffect(() => {
    if (addAccountModalVisible) {
      api.get('/accounts/customer-categories').then(({ data }) => {
        setCustomerCategories(data || []);
      }).catch(() => setCustomerCategories([]));
    }
  }, [addAccountModalVisible]);

  const handleSaveNewAccount = async () => {
    try {
      const values = await accountForm.validateFields();
      setAddAccountSaving(true);
      const trUpper = (s: string | undefined) => (s != null && s !== '' ? String(s).toLocaleUpperCase('tr-TR') : s);
      const payload = {
        firstName: trUpper(values.firstName),
        lastName: trUpper(values.lastName),
        companyName: trUpper(values.companyName),
        accountType: 1,
        customerCategory: values.customerCategory ?? 1,
        taxNumber: values.taxNumber,
        taxOffice: values.taxOffice,
        email: values.email,
        phone: values.phone ? String(values.phone).replace(/\s/g, '') : undefined,
        address: values.address,
        notificationConsent: true,
      };
      const { data } = await api.post('/accounts', payload);
      message.success('Cari hesap eklendi');
      setAddAccountModalVisible(false);
      accountForm.resetFields();
      await loadAccounts();
      if (data && data.id) {
        const newAccount: Account = {
          id: data.id,
          accountCode: data.accountCode,
          accountName: data.accountName,
          firstName: data.firstName,
          lastName: data.lastName,
          companyName: data.companyName,
          accountType: data.accountType || 'Customer',
          customerCategory: data.customerCategory,
          email: data.email,
          phone: data.phone,
        };
        onSelect(newAccount);
        onClose();
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Cari hesap eklenemedi');
    } finally {
      setAddAccountSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const runFilter = async () => {
      if (!searchText) {
        setFilteredAccounts(accounts);
        return;
      }

      const search = trLower(searchText);
      const filtered = accounts.filter(acc =>
        trLower(acc.accountCode).includes(search) ||
        trLower(acc.accountName).includes(search) ||
        trLower(acc.firstName).includes(search) ||
        trLower(acc.lastName).includes(search) ||
        (acc.companyName != null && trLower(acc.companyName).includes(search))
      );

      let vehicleMatches: Account[] = [];
      if (searchText.trim().length >= 3) {
        try {
          const vehicles = await searchCustomerVehicles({ plate: searchText });
          const customerIds = new Set(vehicles.map(v => v.customerId));
          vehicleMatches = accounts.filter(acc => customerIds.has(acc.id));
        } catch (error) {
          console.error('Plate search error:', error);
        }
      }

      if (cancelled) return;
      const merged = new Map<number, Account>();
      filtered.forEach(acc => merged.set(acc.id, acc));
      vehicleMatches.forEach(acc => merged.set(acc.id, acc));
      setFilteredAccounts(Array.from(merged.values()));
    };

    runFilter();
    return () => { cancelled = true; };
  }, [searchText, accounts]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/accounts');
      const list = data || [];
      const byId = new Map<number, Account>();
      list.forEach((acc: Account) => byId.set(acc.id, acc));
      const unique = Array.from(byId.values());
      setAccounts(unique);
      setFilteredAccounts(unique);
      setPlatesByAccountId({});
      setLoadingPlates({});
    } catch (error) {
      console.error('Load accounts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (account?: Account) => {
    const accountToSelect = account || selectedAccount;
    if (accountToSelect) {
      onSelect(accountToSelect);
      onClose();
      setSearchText('');
      setSelectedAccount(null);
    }
  };

  const loadPlatesForAccount = async (accountId: number) => {
    if (loadingPlates[accountId] || platesByAccountId[accountId]) return;
    setLoadingPlates(prev => ({ ...prev, [accountId]: true }));
    try {
      const vehicles = await fetchCustomerVehicles({ customerId: accountId });
      const plates = vehicles
        .filter(v => v.plate)
        .sort((a, b) => b.id - a.id)
        .map(v => v.plate)
        .slice(0, 3);
      setPlatesByAccountId(prev => ({ ...prev, [accountId]: plates }));
    } catch (error) {
      console.error('Load plates error:', error);
      setPlatesByAccountId(prev => ({ ...prev, [accountId]: [] }));
    } finally {
      setLoadingPlates(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const columns: ColumnsType<Account> = [
    {
      title: 'Hesap Kodu',
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 120,
    },
    {
      title: 'Ad Soyad / Firma',
      key: 'displayName',
      render: (_, record: Account) => {
        if (record.companyName) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.companyName}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                {record.firstName} {record.lastName}
              </div>
            </div>
          );
        }
        return `${record.firstName} ${record.lastName}`;
      },
    },
    {
      title: 'Hesap Tipi',
      dataIndex: 'accountType',
      key: 'accountType',
      width: 120,
      render: (type: string) => {
        const typeMap: { [key: string]: { color: string; label: string } } = {
          'Customer': { color: 'blue', label: 'Müşteri' },
          'Supplier': { color: 'green', label: 'Tedarikçi' },
          'Both': { color: 'purple', label: 'İkisi de' }
        };
        const typeInfo = typeMap[type] || { color: 'default', label: type };
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
      },
    },
    {
      title: 'Kategori',
      dataIndex: 'customerCategory',
      key: 'customerCategory',
      width: 130,
      render: (category: string | undefined) => {
        if (!category) return '-';
        const categoryMap: { [key: string]: { color: string; label: string } } = {
          'Retail': { color: 'gold', label: 'Perakende' },
          'Wholesale': { color: 'cyan', label: 'Toptan' }
        };
        const categoryInfo = categoryMap[category] || { color: 'default', label: category };
        return <Tag color={categoryInfo.color}>{categoryInfo.label}</Tag>;
      },
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: 'Son 3 Plaka',
      key: 'lastPlates',
      width: 180,
      render: (_: any, record: Account) => {
        const plates = platesByAccountId[record.id];
        if (!plates) {
          loadPlatesForAccount(record.id);
          return <span>Yükleniyor...</span>;
        }
        return plates.length ? plates.join(', ') : '-';
      },
    },
  ];

  return (
    <Modal
      title="Cari Hesap Seçin"
      open={visible}
      onCancel={() => {
        onClose();
        setSearchText('');
        setSelectedAccount(null);
      }}
      width={900}
      footer={[
        <Button
          key="add-new"
          icon={<PlusOutlined />}
          onClick={() => {
            if (inlineAddAccount) {
              accountForm.resetFields();
              accountForm.setFieldsValue({ customerCategory: 1 });
              setAddAccountModalVisible(true);
            } else {
              onClose();
              window.location.href = '/add-vehicle-with-customer';
            }
          }}
        >
          Yeni Cari Ekle
        </Button>,
        <Button key="cancel" onClick={onClose}>
          İptal
        </Button>,
        <Button 
          key="select" 
          type="primary" 
          onClick={() => handleSelect()}
          disabled={!selectedAccount}
        >
          Seç
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input
          placeholder="Hesap kodu, ad, soyad veya firma adına göre ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        
        <Table
          columns={columns}
          dataSource={filteredAccounts}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kayıt`,
          }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedAccount ? [selectedAccount.id] : [],
            onChange: (_, selectedRows) => {
              setSelectedAccount(selectedRows[0] || null);
            },
          }}
          onRow={(record) => ({
            onClick: () => handleSelect(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Space>

      {/* Yeni Cari Ekle Modal (araç ekle gibi modal içinde) */}
      <Modal
        title="Yeni Cari Hesap Ekle"
        open={addAccountModalVisible}
        onOk={handleSaveNewAccount}
        onCancel={() => {
          setAddAccountModalVisible(false);
          accountForm.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        confirmLoading={addAccountSaving}
        width={500}
        destroyOnClose
      >
        <Form form={accountForm} layout="vertical">
          <Form.Item
            name="firstName"
            label="Ad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Ad giriniz' }]}
          >
            <Input placeholder="Ad" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Soyad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Soyad giriniz' }]}
          >
            <Input placeholder="Soyad" />
          </Form.Item>
          <Form.Item
            name="companyName"
            label="Firma Adı (Opsiyonel)"
            normalize={trUpperNormalize}
          >
            <Input placeholder="Firma adı (varsa)" />
          </Form.Item>
          <Form.Item name="customerCategory" label="Müşteri Kategorisi">
            <Select placeholder="Kategori seçin" options={customerCategories.map(c => ({ value: c.value, label: c.displayName }))} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Telefon"
            normalize={(val: string) => {
              const digits = (val || '').replace(/\D/g, '').slice(0, 10);
              if (!digits) return '';
              return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
                [a, b, c, d].filter(Boolean).join(' ')
              );
            }}
          >
            <Input placeholder="532 000 00 00" maxLength={14} />
          </Form.Item>
          <Form.Item name="email" label="E-posta" rules={[{ type: 'email', message: 'Geçerli e-posta giriniz' }]}>
            <Input placeholder="E-posta" />
          </Form.Item>
          <Form.Item name="address" label="Adres">
            <Input.TextArea placeholder="Adres" rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default AccountSelectModal;

